import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3,
  TrendingUp,
  Clock,
  Calendar,
  MessageSquare,
  Reply
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

import apiService from '../../services/api';

import MessageChart from './Analytics/MessageChart';
import ResponseTimeChart from './Analytics/ResponseTimeChart';
import CampaignStats from './Analytics/CampaignStats';
import ExportButton from './Analytics/ExportButton';

// Cache para dados de analytics do CRM
const crmAnalyticsCache = new Map();
const CACHE_DURATION = 3 * 60 * 1000; // 3 minutos

// Hook para debounce
const useDebounce = (callback, delay) => {
  const [debounceTimer, setDebounceTimer] = useState(null);

  const debouncedCallback = useCallback((...args) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    const newTimer = setTimeout(() => {
      callback(...args);
    }, delay);
    
    setDebounceTimer(newTimer);
  }, [callback, delay, debounceTimer]);

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  return debouncedCallback;
};

const AnalyticsTab = ({ onError, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('30'); // dias
  const [lastUpdated, setLastUpdated] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({
    messagesByDay: [],
    responseTimes: [],
    campaignStats: []
  });

  // Função para verificar cache
  const getCachedData = useCallback((cacheKey) => {
    const cached = crmAnalyticsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }, []);

  // Função para salvar no cache
  const setCachedData = useCallback((cacheKey, data) => {
    crmAnalyticsCache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
  }, []);

  // Carregar dados de analytics com cache
  const loadAnalyticsData = useCallback(async (forceRefresh = false) => {
    const cacheKey = `crm_analytics_${period}`;
    
    // Verificar cache se não for refresh forçado
    if (!forceRefresh) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        setAnalyticsData(cachedData);
        setLastUpdated(new Date());
        return;
      }
    }

    setLoading(true);
    try {
      // Buscar dados em paralelo com timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 8000)
      );

      const dataPromises = [
        apiService.request(`/analytics/messages-by-day?days=${period}`),
        apiService.request(`/analytics/response-times?days=${period}`),
        apiService.request(`/analytics/campaign-stats?days=${period}`)
      ];

      const [messagesData, responseTimesData, campaignStatsData] = await Promise.race([
        Promise.all(dataPromises),
        timeoutPromise
      ]);

      if (messagesData.success && responseTimesData.success && campaignStatsData.success) {
        const newData = {
          messagesByDay: messagesData.data || [],
          responseTimes: responseTimesData.data || [],
          campaignStats: campaignStatsData.data || []
        };
        
        setAnalyticsData(newData);
        setCachedData(cacheKey, newData);
        setLastUpdated(new Date());
      } else {
        throw new Error('Erro ao carregar dados de analytics');
      }

    } catch (error) {

      if (error.message === 'Timeout') {
        onError('Timeout ao carregar dados. Tente novamente.');
      } else {
        onError('Erro ao carregar dados de analytics');
      }
    } finally {
      setLoading(false);
    }
  }, [period, onError, getCachedData, setCachedData]);

  // Exportar dados em CSV
  const handleExport = async (type) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiService.baseURL}/analytics/export-csv?type=${type}&days=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        // Notificação removida
      } else {
        throw new Error('Erro ao exportar dados');
      }
    } catch (error) {

      onError('Erro ao exportar dados');
    }
  };

  // Debounce para mudança de período
  const debouncedLoadData = useDebounce(loadAnalyticsData, 300);

  // Carregar dados quando o período mudar (com debounce)
  useEffect(() => {
    debouncedLoadData();
  }, [period, debouncedLoadData]);

  // Carregar dados iniciais
  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  // Calcular estatísticas resumidas com memoização
  const stats = useMemo(() => {
    const totalSent = analyticsData.messagesByDay.reduce((sum, day) => sum + (day.messages_sent || 0), 0);
    const totalResponded = analyticsData.messagesByDay.reduce((sum, day) => sum + (day.messages_responded || 0), 0);
    const responseRate = totalSent > 0 ? ((totalResponded / totalSent) * 100).toFixed(1) : 0;
    
    // Melhor horário para envio
    const bestHour = analyticsData.responseTimes.reduce((best, current) => 
      (current.response_rate || 0) > (best?.response_rate || 0) ? current : best, null
    );

    return {
      totalSent,
      totalResponded,
      responseRate,
      bestHour: bestHour ? `${bestHour.hour_label}` : 'N/A',
      totalCampaigns: analyticsData.campaignStats.length
    };
  }, [analyticsData]);

  // Componente de skeleton loading
  const SkeletonCard = () => (
    <div className={`p-4 rounded-lg border ${
      isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    }`}>
      <div className="animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="w-6 h-6 bg-gray-300 rounded"></div>
          <div className="w-16 h-4 bg-gray-300 rounded"></div>
        </div>
        <div className="w-20 h-8 bg-gray-300 rounded mb-2"></div>
        <div className="w-32 h-4 bg-gray-300 rounded"></div>
      </div>
    </div>
  );

  const SkeletonChart = () => (
    <div className={`p-6 rounded-lg border ${
      isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    }`}>
      <div className="animate-pulse">
        <div className="w-48 h-6 bg-gray-300 rounded mb-4"></div>
        <div className="w-full h-64 bg-gray-300 rounded"></div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header com filtros */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Analytics
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Análise de desempenho das suas campanhas
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Filtro de período */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              disabled={loading}
              className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <option value="7">Últimos 7 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="90">Últimos 90 dias</option>
            </select>
          </div>
          
          {lastUpdated && !loading && (
            <span className="text-xs text-gray-500">
              Atualizado: {lastUpdated.toLocaleTimeString()}
            </span>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={() => loadAnalyticsData(true)}
              disabled={loading}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                isDarkMode
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Atualizando...' : 'Atualizar'}
            </button>
            
            <ExportButton 
              onExport={handleExport}
              onError={onError}
              onSuccess={onSuccess}
            />
          </div>
        </div>
      </div>

      {/* Cards de estatísticas resumidas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {loading ? (
          Array.from({ length: 5 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))
        ) : (
          <>
            <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border transition-all hover:shadow-md`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Mensagens Enviadas</p>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.totalSent.toLocaleString()}</p>
                </div>
                <MessageSquare className={`w-8 h-8 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
            </div>

            <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border transition-all hover:shadow-md`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Respostas</p>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.totalResponded.toLocaleString()}</p>
                </div>
                <Reply className={`w-8 h-8 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
            </div>

            <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border transition-all hover:shadow-md`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Taxa de Resposta</p>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.responseRate}%</p>
                </div>
                <TrendingUp className={`w-8 h-8 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
            </div>

            <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border transition-all hover:shadow-md`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Melhor Horário</p>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.bestHour}</p>
                </div>
                <Clock className={`w-8 h-8 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
            </div>

            <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border transition-all hover:shadow-md`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Campanhas</p>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.totalCampaigns}</p>
                </div>
                <BarChart3 className={`w-8 h-8 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {loading ? (
          <>
            <SkeletonChart />
            <SkeletonChart />
          </>
        ) : (
          <>
            {/* Mensagens por dia */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 transition-all hover:shadow-md">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Mensagens por Dia
              </h3>
              <MessageChart data={analyticsData.messagesByDay} loading={loading} />
            </div>

            {/* Horários de resposta */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 transition-all hover:shadow-md">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Taxa de Resposta por Horário
              </h3>
              <ResponseTimeChart data={analyticsData.responseTimes} loading={loading} />
            </div>
          </>
        )}
      </div>

      {/* Estatísticas de campanhas */}
      {loading ? (
        <SkeletonChart />
      ) : (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 transition-all hover:shadow-md">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Estatísticas de Campanhas
          </h3>
          <CampaignStats data={analyticsData.campaignStats} loading={loading} />
        </div>
      )}

      {/* Tabela de campanhas */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Últimas Campanhas
          </h3>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : analyticsData.campaignStats.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Enviadas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Respostas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Taxa
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {analyticsData.campaignStats.slice(0, 10).map((campaign) => (
                  <tr key={campaign.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {campaign.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        campaign.status === 'completed' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : campaign.status === 'active'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : campaign.status === 'stopped'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : campaign.status === 'paused'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}>
                        {campaign.status === 'completed' ? 'Concluída' :
                         campaign.status === 'active' ? 'Ativa' :
                         campaign.status === 'stopped' ? 'Parada' :
                         campaign.status === 'paused' ? 'Pausada' :
                         campaign.status === 'pending' ? 'Pendente' :
                         campaign.status === 'failed' ? 'Falhou' :
                         campaign.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {campaign.sent_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {campaign.response_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {campaign.response_rate}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex items-center justify-center h-32 text-gray-500 dark:text-gray-400">
              Nenhuma campanha encontrada
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;